import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SignaturePad from '@/components/SignaturePad';
import { Camera, X, Loader2, Check, User, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function PODCapture({ loadId, onComplete, onCancel }) {
  const { token, user } = useAuth();
  const [receiverName, setReceiverName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, { file, preview: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const uploadToStorage = async (dataOrFile, path) => {
    let blob;
    if (typeof dataOrFile === 'string' && dataOrFile.startsWith('data:')) {
      const res = await fetch(dataOrFile);
      blob = await res.blob();
    } else {
      blob = dataOrFile;
    }
    const { data, error } = await supabase.storage.from('uploads').upload(path, blob, { upsert: true });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleSubmit = async () => {
    if (!receiverName.trim()) {
      toast.error('Receiver name is required');
      return;
    }
    setSubmitting(true);
    try {
            const ts = Date.now();

      // Upload signature
      let signatureUrl = null;
      if (signatureData) {
        signatureUrl = await uploadToStorage(signatureData, `pod/${loadId}/signature_${ts}.png`);
      }

      // Upload photos
      const photoUrls = [];
      for (let i = 0; i < photos.length; i++) {
        const ext = photos[i].file.name.split('.').pop() || 'jpg';
        const url = await uploadToStorage(photos[i].file, `pod/${loadId}/photo_${ts}_${i}.${ext}`);
        photoUrls.push(url);
      }

      // Submit POD
      await api.post(`/loads/${loadId}/pod`, {
        receiver_name: receiverName,
        signature_url: signatureUrl,
        photo_urls: photoUrls,
        notes: notes || undefined,
      });

      // Mark load as completed
      await api.put(`/loads/${loadId}/status`, { status: 'completed' });

      toast.success('Delivery confirmed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit POD');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
        <button onClick={onCancel} className="text-muted-foreground"><X className="w-5 h-5" /></button>
        <h2 className="text-sm font-bold text-foreground">Proof of Delivery</h2>
        <div className="w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 py-4 space-y-5">
        {/* Receiver Name */}
        <div>
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <User className="w-3 h-3" /> Receiver Name <span className="text-red-500">*</span>
          </label>
          <Input value={receiverName} onChange={e => setReceiverName(e.target.value)}
            placeholder="Who received the delivery?"
            className="border-input text-foreground h-10" />
        </div>

        {/* Signature */}
        <div>
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <FileText className="w-3 h-3" /> Signature
          </label>
          <SignaturePad onSave={setSignatureData} width={350} height={150} className="[&_canvas]:!bg-background [&_canvas]:border-input" />
        </div>

        {/* Photos */}
        <div>
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <Camera className="w-3 h-3" /> Photos
          </label>
          <div className="flex gap-2 flex-wrap">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border">
                <img src={p.preview} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(i)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            <button onClick={() => fileRef.current?.click()}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-input flex flex-col items-center justify-center text-muted-foreground hover:border-muted-foreground">
              <Camera className="w-5 h-5" />
              <span className="text-[9px] mt-0.5">Add</span>
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple
            onChange={handlePhotoCapture} className="hidden" />
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Any delivery issues or comments..."
            className="w-full border border-input rounded-lg px-3 py-2 text-sm text-foreground bg-background h-20 resize-none" />
        </div>
      </div>

      {/* Submit */}
      <div className="px-4 py-3 border-t bg-card">
        <Button onClick={handleSubmit} disabled={submitting}
          className="w-full bg-emerald-600 text-white font-bold h-12 text-sm gap-2">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          {submitting ? 'Submitting...' : 'Submit & Complete Delivery'}
        </Button>
      </div>
    </div>
  );
}
